import { ProductDetail } from '@grpc-clients/external/mercury/v1/attraction/common/product-detail.pb';
import { createSelector } from '@ngrx/store';
import { ActivitiesOrdering } from '@shared/models/activities.model';
import { ActivityFilter } from '@shared/models/filter.model';
import { filterTimeBetweenList, filterTimeBetweenValue } from '@shared/shared/helpers/filter';
import { floorStr } from '@shared/utils/floor';
import * as fromReducers from '../reducers';
import * as fromActivitiesSearch from '../reducers/activities.reducer';

export const getActivitiesSearchFeatureState = createSelector(
  fromReducers.getTravelSearchState,
  (state: fromReducers.TravelSearchState) => state.activitiesSearch
);

export const getActivitiesSearchData = createSelector(
  getActivitiesSearchFeatureState,
  fromActivitiesSearch.getActivitiesData
);

export const getActivitiesLoaded = createSelector(
  getActivitiesSearchFeatureState,
  fromActivitiesSearch.getActivitiesLoaded
);

export const getActivitiesLoading = createSelector(
  getActivitiesSearchFeatureState,
  fromActivitiesSearch.getActivitiesLoading
);
export const getActivitiesSortType = createSelector(
  getActivitiesSearchFeatureState,
  fromActivitiesSearch.getActivitiesSortType
);

export const getActivitiesPaging = createSelector(getActivitiesSearchFeatureState, (state) => state.paging);

export const getActivitiesFilterState = createSelector(getActivitiesSearchFeatureState, (state) => state.filterState);
export const getValidatedActivitiesFilterState = createSelector(
  getActivitiesSearchData,
  getActivitiesFilterState,
  (activities, filter) => {
    const categories = filter.categories?.filter(
      (category) => !filter.specials.some((special) => special.value === category.value)
    );
    return disableFilters(activities, { ...filter, categories });
  }
);
export const getSelectedCategoryFilters = createSelector(getValidatedActivitiesFilterState, (state) =>
  state.categories?.filter((filter) => filter.selected)
);
export const getUnselectedCategoryFilters = createSelector(getValidatedActivitiesFilterState, (state) =>
  state.categories?.filter((filter) => !filter.selected && filter.enabled)
);

export const getSortedAndFilteredActivities = createSelector(
  getActivitiesSearchData,
  getActivitiesSortType,
  getValidatedActivitiesFilterState,
  (activities, sort, filter) => {
    const sortedActivities = activities
      .map((activity) => {
        return activity.reviewRating
          ? ({ ...activity, reviewRating: { value: floorStr(activity.reviewRating.value, 1) } } as ProductDetail)
          : activity;
      })
      .sort((a: ProductDetail, b: ProductDetail) => sortActivities(a, b, sort));
    if (filter) {
      const filteredActivities = filterActivitiesResults(sortedActivities, filter);
      return filteredActivities;
    } else {
      return sortedActivities;
    }
  }
);

export function filterActivitiesResults(
  activities: ProductDetail[],
  filters: fromActivitiesSearch.ActivityFilters,
  skippedFilterName?: string
) {
  const price = filters.price;
  const ratings = filters.ratings.filter((item) => item.selected);
  const timeOfDays = filters.timeOfDays.filter((item) => item.selected);
  const durations = filters.durations.filter((item) => item.selected);
  const specials = filters.specials.filter((item) => item.selected);
  const categories = filters.categories?.filter((item) => item.selected);
  let filteredActivities = activities;
  filteredActivities = checkFilterByName(filters.searchByName, filteredActivities);

  if (price && skippedFilterName !== 'price') {
    filteredActivities = filteredActivities.filter((activity) => {
      const priceValue = +activity?.fromPrice?.value || 0;
      const inPriceRange = price[0] <= priceValue && priceValue <= price[1];
      return inPriceRange;
    });
  }

  if (durations?.length && skippedFilterName !== 'durations') {
    const durationRanges = durations.map((filter) =>
      filter.value.split('-').map((value) => Number.parseInt(value, 10) * 60)
    );

    filteredActivities = filteredActivities.filter((activity) => {
      const durationFromMinutes = activity.durationFromMinutes?.value;
      const durationToMinutes = activity.durationToMinutes?.value;

      if (durationFromMinutes === undefined && durationToMinutes === undefined) {
        return false;
      }

      return durationRanges.some((range) => {
        const activityMinInRange = range[0] <= durationFromMinutes && (!range[1] || durationFromMinutes <= range[1]);
        const activityMaxInRange = range[0] <= durationToMinutes && (!range[1] || durationToMinutes <= range[1]);
        const rangeIncluded = durationFromMinutes <= range[0] && range[1] <= durationToMinutes;

        return activityMinInRange || activityMaxInRange || rangeIncluded;
      });
    });
  }

  if (ratings?.length > 0 && skippedFilterName !== 'ratings') {
    filteredActivities = filteredActivities.filter((activity) => {
      const ratingValue = Math.floor(+activity?.reviewRating?.value);
      return ratings.some((filter) => +filter.value === ratingValue);
    });
  }

  if (timeOfDays?.length > 0 && skippedFilterName !== 'timeOfDays') {
    filteredActivities = filteredActivities.filter((activity) => {
      for (const filter of timeOfDays) {
        const [startTime, endTime] = filter.value.split('-');

        if (activity.timedEntryList) {
          for (const timedEntry of activity.timedEntryList) {
            if (filterTimeBetweenList(startTime, endTime, timedEntry)) {
              return true;
            }
          }
        }

        if (activity?.opensAt?.value && activity?.closesAt?.value && !activity?.timedEntryList?.length) {
          if (filterTimeBetweenValue(startTime, endTime, activity.opensAt.value, activity?.closesAt?.value)) {
            return true;
          }
        }
      }

      return false;
    });
  }

  if (specials?.length && skippedFilterName !== 'specials') {
    filteredActivities = filteredActivities.filter((activity) =>
      activity.attributeIdList.some((id) => specials.some((filter) => filter.value === id))
    );
  }

  if (categories?.length && skippedFilterName !== 'categories') {
    filteredActivities = filteredActivities.filter((activity) =>
      activity.attributeIdList.some((id) => categories.some((filter) => filter.value === id))
    );
  }

  return filteredActivities;
}

export function checkFilterByName(searchByName: string, filteredActivities: ProductDetail[]) {
  if (searchByName) {
    filteredActivities = filteredActivities.filter((activity) => {
      return activity.productName.toLowerCase().match(searchByName.toLowerCase());
    });
  }
  return filteredActivities;
}

export const getSortedActivitiesPrices = createSelector(getActivitiesSearchData, (activities) =>
  [...activities]
    .sort((a, b) => sortActivities(a, b, ActivitiesOrdering.ASCENDING))
    .map((activity) => +activity.fromPrice.value)
);

export const getSelectableActivitiesPrice = createSelector(getSortedActivitiesPrices, (sortedPrices) => {
  const index = Math.ceil(sortedPrices.length * 0.8);
  const minPrice = sortedPrices[0];
  const flooredMaxPrice = Math.floor(sortedPrices[index - 1] / 10) * 10;
  const maxPrice = flooredMaxPrice < minPrice ? minPrice : flooredMaxPrice;

  return [minPrice, maxPrice] as [number, number];
});

export const getFullRangeSelectable = createSelector(
  getSortedActivitiesPrices,
  getSelectableActivitiesPrice,
  (sortedPrices, selectablePrice) => sortedPrices[sortedPrices.length - 1] === selectablePrice[1]
);

export const getSelectedActivitiesPrice = createSelector(
  getSelectableActivitiesPrice,
  getValidatedActivitiesFilterState,
  (selectablePrice, filter) => {
    let selectedPrice = selectablePrice;

    if (filter?.price) {
      const minPrice = filter.price[0];
      const maxPrice = filter.price[1] < selectablePrice[1] ? filter.price[1] : selectablePrice[1];
      selectedPrice = [minPrice, maxPrice];
    }
    return selectedPrice;
  }
);

export const getMultiplePricesAvailable = createSelector(getActivitiesSearchData, (activities) =>
  activities.some((activity) => +activity.fromPrice.value !== +activities[0].fromPrice.value)
);

export const getSearchByNameFilter = createSelector(getValidatedActivitiesFilterState, (state) => state?.searchByName);
export const getRatingFilter = createSelector(getValidatedActivitiesFilterState, (state) => state?.ratings);
export const getTimeOfDayFilter = createSelector(getValidatedActivitiesFilterState, (state) => state?.timeOfDays);
export const getDurationFilter = createSelector(getValidatedActivitiesFilterState, (state) => state?.durations);
export const getSpecialFilter = createSelector(getValidatedActivitiesFilterState, (state) => state?.specials);
export const getCategoriesFilter = createSelector(getValidatedActivitiesFilterState, (state) => state?.categories);

export const getSelectedFilterCount = createSelector(
  getValidatedActivitiesFilterState,
  getSelectableActivitiesPrice,
  (filter, defaultPrice) => getSelectedFiltersNumber(filter, defaultPrice)
);

export const getSelectedFilterCountIncludingDisabled = createSelector(
  getValidatedActivitiesFilterState,
  getSelectableActivitiesPrice,
  (filter, defaultPrice) => getSelectedFiltersNumber(filter, defaultPrice, false)
);

export const getSelectedFilterCountBreakDown = createSelector(
  getValidatedActivitiesFilterState,
  getSelectableActivitiesPrice,
  (filter, defaultPrice) => getSelectedFiltersNumberBeakDown(filter, defaultPrice)
);

export const getActivitiesWithoutFilterCount = createSelector(
  getActivitiesSearchFeatureState,
  (result) => result.activities.length
);

export const getSpecialFilterByName = (name: string) =>
  createSelector(getSpecialFilter, (specialFilters) => specialFilters.find((filter) => filter.name === name)?.value);

export const getAttractionByProductId = (productId: number) =>
  createSelector(getActivitiesSearchData, (activities) => {
    return activities.find((item) => item.productId === productId);
  });

export const getCurrencyCodeByProductId = (productId: number) =>
  createSelector(getActivitiesSearchData, (activities) =>
    activities.length ? activities.find((activity) => activity.productId === productId).currencyCode : undefined
  );

function sortActivities(a: ProductDetail, b: ProductDetail, orderType: number) {
  const lowestActivitiesPrice1 = +a.fromPrice.value;
  const lowestActivitiesPrice2 = +b.fromPrice.value;
  const lowestActivitiesRating1 = +a.reviewRating?.value || 0;
  const lowestActivitiesRating2 = +b.reviewRating?.value || 0;
  const lowestActivitiesRanking1 = +a.ranking?.value || 0;
  const lowestActivitiesRanking2 = +b.ranking?.value || 0;
  const lowestActivitiesRatingCount1 = a.totalReviews;
  const lowestActivitiesRatingCount2 = b.totalReviews;
  switch (orderType) {
    case ActivitiesOrdering.ASCENDING:
      return lowestActivitiesPrice1 - lowestActivitiesPrice2;
    case ActivitiesOrdering.DESCENDING:
      return lowestActivitiesPrice2 - lowestActivitiesPrice1;
    case ActivitiesOrdering.RATINGS: {
      const groupPriorityOrderDiff = lowestActivitiesRating2 - lowestActivitiesRating1;
      if (groupPriorityOrderDiff === 0) {
        const ratingCountDiff = lowestActivitiesRatingCount2 - lowestActivitiesRatingCount1;
        if (ratingCountDiff === 0) {
          return lowestActivitiesPrice1 - lowestActivitiesPrice2;
        }
        return ratingCountDiff;
      }
      return groupPriorityOrderDiff;
    }
    case ActivitiesOrdering.RECOMMENDED: {
      const ratingDiff = lowestActivitiesRanking2 - lowestActivitiesRanking1;
      if (ratingDiff === 0) {
        return lowestActivitiesRanking1 - lowestActivitiesRanking2;
      }
      return ratingDiff;
    }
    default:
      return 0;
  }
}

function disableFilters(
  activities: ProductDetail[],
  filterState: fromActivitiesSearch.ActivityFilters
): fromActivitiesSearch.ActivityFilters {
  const filteredActivitiesNoRating = filterActivitiesResults(activities, filterState, 'ratings');
  const filteredActivitiesNoDurations = filterActivitiesResults(activities, filterState, 'durations');
  const filteredActivitiesNoTimeOfDays = filterActivitiesResults(activities, filterState, 'timeOfDays');
  const filteredActivitiesNoSpecials = filterActivitiesResults(activities, filterState, 'specials');
  const filteredActivitiesNoCategories = filterActivitiesResults(activities, filterState, 'categories');

  return {
    ...filterState,
    ratings: filterState.ratings.map((rating) => ({
      ...rating,
      enabled: someActivities(filteredActivitiesNoRating, rating, 'ratings'),
    })),
    durations: filterState.durations.map((duration) => ({
      ...duration,
      enabled: someActivities(filteredActivitiesNoDurations, duration, 'durations'),
    })),
    timeOfDays: filterState.timeOfDays.map((timeOfDay) => ({
      ...timeOfDay,
      enabled: someActivities(filteredActivitiesNoTimeOfDays, timeOfDay, 'timeOfDays'),
    })),
    specials: filterState.specials.map((special) => ({
      ...special,
      enabled: someActivities(filteredActivitiesNoSpecials, special, 'specials'),
    })),
    categories: filterState.categories?.map((special) => ({
      ...special,
      enabled: someActivities(filteredActivitiesNoCategories, special, 'categories'),
    })),
  };
}

function someActivities(
  activities: ProductDetail[],
  filter: ActivityFilter<string | number>,
  filterName: string
): boolean {
  switch (filterName) {
    case 'ratings': {
      return activities.some((activity) => Math.floor(+activity.reviewRating?.value) === +filter.value);
    }
    case 'timeOfDays':
      return activities.some((activity) => {
        const [startTime, endTime] = (filter as ActivityFilter<string>).value.split('-');

        if (activity.timedEntryList) {
          for (const timedEntry of activity.timedEntryList) {
            if (filterTimeBetweenList(startTime, endTime, timedEntry)) {
              return true;
            }
          }
        }

        if (activity?.opensAt?.value && activity?.closesAt?.value && !activity?.timedEntryList?.length) {
          if (filterTimeBetweenValue(startTime, endTime, activity.opensAt.value, activity?.closesAt?.value)) {
            return true;
          }
        }

        return false;
      });
    case 'durations': {
      const timeRange = (filter as ActivityFilter<string>).value.split('-').map((value) => +value * 60);
      return activities.some((activity) => {
        const durationFromMinutes = activity.durationFromMinutes?.value;
        const durationToMinutes = activity.durationToMinutes?.value;

        if (durationFromMinutes === undefined && durationToMinutes === undefined) {
          return false;
        }

        const activityMinInRange =
          timeRange[0] <= durationFromMinutes && (!timeRange[1] || durationFromMinutes <= timeRange[1]);
        const activityMaxInRange =
          timeRange[0] <= durationToMinutes && (!timeRange[1] || durationToMinutes <= timeRange[1]);
        const rangeIncluded = durationFromMinutes >= timeRange[0] && timeRange[1] >= durationToMinutes;

        return activityMinInRange || activityMaxInRange || rangeIncluded;
      });
    }
    case 'specials':
      return activities.some((activity) => activity.attributeIdList.includes((filter as ActivityFilter<number>).value));
    case 'categories':
      return activities.some((activity) => activity.attributeIdList.includes((filter as ActivityFilter<number>).value));
    default:
      return true;
  }
}

function getSelectedFiltersNumber(
  filter: fromActivitiesSearch.ActivityFilters,
  defaultPrice: [number, number],
  ignoreDisabled = true
) {
  return Object.entries(filter).reduce((prev: number, curr) => {
    let increment = 0;
    if (curr[0] === 'searchByName') {
      increment = !curr[1] ? 0 : 1;
      return prev + increment;
    }
    if (curr[0] === 'price') {
      increment = !curr[1] || (defaultPrice[0] === curr[1][0] && defaultPrice[1] === curr[1][1]) ? 0 : 1;
      return prev + increment;
    }
    increment = !curr[1] ? 0 : curr[1].filter((a) => a.selected && (ignoreDisabled ? a.enabled : true)).length;
    return prev + increment;
  }, 0);
}

function getSelectedFiltersNumberBeakDown(
  filters: fromActivitiesSearch.ActivityFilters,
  defaultPrice: [number, number]
) {
  return {
    ratings: filters.ratings.reduce(reducerFn, 0),
    categories: filters.categories.reduce(reducerFn, 0),
    specials: filters.specials.reduce(reducerFn, 0),
    timeOfDays: filters.timeOfDays.reduce(reducerFn, 0),
    durations: filters.durations.reduce(reducerFn, 0),
    price: calculatePriceChangeCount(filters.price, defaultPrice),
  };
}

function reducerFn(prev: number, curr: ActivityFilter) {
  return prev + (curr.selected && curr.enabled ? 1 : 0);
}

function calculatePriceChangeCount(price: [number, number], defaultPrice: [number, number]) {
  if (!price) {
    return 0;
  }
  return price[0] !== defaultPrice[0] || price[1] !== defaultPrice[1];
}
